import { useAuth } from "@/App";
import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { 
  HelpCircle, 
  Mail, 
  LayoutDashboard, 
  FileText, 
  Calendar, 
  Settings, 
  Users, 
  CheckCircle, 
  XCircle, 
  Plus,
  Eye,
  Shield,
  Bell,
  Palette,
  Clock,
  Flag,
  Building,
  CreditCard,
  Crown,
  Briefcase,
  Key,
  AlertTriangle
} from "lucide-react";

// Sections that show a SectionInfoBanner. Keep this list in sync with SectionInfoBanner usages.
const BANNER_SECTIONS = [
  { key: "richieste", labelByRole: { dipendente: "Home" }, defaultLabel: "Richieste", icon: FileText, roles: ["titolare", "amministrativo", "dipendente"] },
  { key: "calendario", defaultLabel: "Calendario", icon: Calendar, roles: ["titolare", "amministrativo", "dipendente"] },
  { key: "elenco-dipendenti", defaultLabel: "Elenco dipendenti", icon: Users, roles: ["titolare"], basePlanOnly: true },
  { key: "abbonamento", defaultLabel: "Abbonamento", icon: CreditCard, roles: ["titolare"] },
];

function BannerVisibilityCard({ user, isBasePlan }) {
  const [states, setStates] = useState({});

  // Determine which sections are visible to the current user
  const visibleSections = BANNER_SECTIONS.filter((s) => {
    if (!s.roles.includes(user?.ruolo)) return false;
    if (s.basePlanOnly && !isBasePlan) return false;
    return true;
  });

  const refresh = () => {
    if (!user?.id) return;
    const next = {};
    visibleSections.forEach((s) => {
      const v = localStorage.getItem(`section_help_${user.id}_${s.key}`);
      // null = mai scelto -> banner visibile by default
      next[s.key] = v === null ? true : v === "1";
    });
    setStates(next);
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, isBasePlan]);

  const setSection = (key, on) => {
    if (!user?.id) return;
    localStorage.setItem(`section_help_${user.id}_${key}`, on ? "1" : "0");
    setStates((prev) => ({ ...prev, [key]: on }));
  };

  const reactivateAll = () => {
    if (!user?.id) return;
    visibleSections.forEach((s) => {
      localStorage.setItem(`section_help_${user.id}_${s.key}`, "1");
    });
    refresh();
    toast.success("Tutti i banner sono stati riattivati");
  };

  return (
    <Card className="border-2" style={{ borderColor: "var(--theme-200)", backgroundColor: "var(--theme-50)" }} data-testid="banner-visibility-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-3 text-lg">
          <div className="p-2 rounded-lg" style={{ backgroundColor: "var(--theme-100)" }}>
            <Eye className="h-5 w-5" style={{ color: "var(--theme-600)" }} />
          </div>
          Banner istruzioni nelle pagine
        </CardTitle>
        <CardDescription className="text-slate-600">
          Riattiva i banner di istruzioni che hai disattivato in ogni pagina dell'app.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <ul className="space-y-2">
          {visibleSections.map((s) => {
            const Icon = s.icon;
            const on = !!states[s.key];
            const label = (s.labelByRole && s.labelByRole[user?.ruolo]) || s.defaultLabel;
            return (
              <li
                key={s.key}
                className="flex items-center justify-between gap-3 p-3 rounded-lg bg-white border border-slate-200"
                data-testid={`banner-toggle-row-${s.key}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-8 w-8 rounded-md flex items-center justify-center shrink-0" style={{ backgroundColor: "var(--theme-100)" }}>
                    <Icon className="h-4 w-4" style={{ color: "var(--theme-600)" }} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{label}</p>
                    <p className="text-xs text-slate-500">{on ? "Banner visibile" : "Banner nascosto"}</p>
                  </div>
                </div>
                <Switch
                  checked={on}
                  onCheckedChange={(v) => setSection(s.key, v)}
                  data-testid={`banner-toggle-${s.key}`}
                />
              </li>
            );
          })}
        </ul>
        <div className="flex justify-end pt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={reactivateAll}
            data-testid="banner-reactivate-all-btn"
          >
            Riattiva tutti
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function InstructionsPage() {
  const { user, settings } = useAuth();
  const ruolo = user?.ruolo;
  const isBasePlan = (settings?.piano || "base") === "base";

  // Instructions for Dipendente on Base plan (no Impostazioni; Home = ex Richieste)
  const dipendenteBaseInstructions = [
    {
      icon: LayoutDashboard,
      title: "Home",
      description: "La tua pagina principale: invia richieste e controlla il loro stato.",
      items: [
        "Visualizza lo storico delle tue richieste",
        "Filtra per stato: Tutte / In attesa / Approvate / Rifiutate",
        "Invia nuove richieste di ferie o permessi con il pulsante '+ Invia una richiesta di ferie o permesso'",
        "Cancella le richieste in attesa se hai commesso errori e reinvia la richiesta corretta",
        "Quando il titolare rifiuta una richiesta puoi leggere la sua eventuale motivazione direttamente sotto la richiesta"
      ]
    },
    {
      icon: Calendar,
      title: "Calendario",
      description: user?.private_calendar
        ? "Il tuo calendario privato:"
        : "Il calendario condiviso del team:",
      items: user?.private_calendar
        ? [
          "Vedi solo le tue ferie e i tuoi permessi approvati",
          "Consulti le festività nazionali",
          "Il titolare ha attivato per te il calendario privato: non vedi le richieste dei colleghi e loro non vedono le tue"
        ]
        : [
          "Vedi le ferie e i permessi approvati del team",
          "Consulti le festività nazionali",
          "Sai sempre chi è assente"
        ]
    },
    {
      icon: HelpCircle,
      title: "Istruzioni",
      description: "La pagina che stai leggendo ora:",
      items: [
        "In cima trovi il pannello 'Banner istruzioni nelle pagine': se hai disattivato un banner di aiuto in una pagina, qui puoi riattivarlo",
        "Più in basso trovi questa guida riassuntiva su come usare l'app"
      ]
    }
  ];

  // Instructions for Titolare on Base plan (semplificato)
  const titolareBaseInstructions = [
    {
      icon: LayoutDashboard,
      title: "Home",
      description: "Il punto di partenza della tua azienda:",
      items: [
        "Al primo accesso trovi in evidenza il Codice Azienda da condividere con i tuoi dipendenti",
        "Negli accessi successivi trovi un riepilogo compatto del codice, sempre disponibile",
        "Vedi quante richieste sono in attesa di approvazione",
        "Accedi rapidamente alla schermata per approvare o rifiutare le richieste"
      ]
    },
    {
      icon: Key,
      title: "Codice Azienda — come usarlo",
      description: "Il modo per far entrare i dipendenti nella tua azienda:",
      items: [
        "Riporta il codice e condividilo con i tuoi dipendenti via WhatsApp, email o di persona",
        "I tuoi dipendenti scaricano l'app Ferie e Permessi sul telefono",
        "Si registrano come dipendenti inserendo questo codice",
        "Una volta registrati fanno subito parte della tua azienda — non devi fare altro"
      ]
    },
    {
      icon: FileText,
      title: "Richieste",
      description: "Gestione delle richieste del team:",
      items: [
        "Visualizza tutte le richieste di ferie e permessi dei tuoi dipendenti",
        "Filtra per stato: In attesa / Approvate / Rifiutate",
        "Approva o rifiuta direttamente dalla lista con un click",
        "Aggiungi una nota di risposta se vuoi spiegare il motivo"
      ]
    },
    {
      icon: Users,
      title: "Elenco dipendenti",
      description: "Tieni sotto controllo chi fa parte della tua azienda:",
      items: [
        "Vedi tutti i dipendenti che si sono registrati con il tuo codice",
        "Quando un dipendente lascia l'azienda, premi il cestino rosso per eliminarlo",
        "L'app ti chiede sempre conferma prima di eliminare (puoi annullare con 'No')",
        "Eliminando un dipendente liberi un posto sul tuo piano (Base = fino a 5 dipendenti)"
      ]
    },
    {
      icon: Calendar,
      title: "Calendario",
      description: "Il calendario condiviso con i tuoi dipendenti:",
      items: [
        "Vedi tutte le ferie e i permessi approvati del team in un colpo d'occhio",
        "Anche i dipendenti vedono lo stesso calendario, così sanno chi è assente",
        "Consulta le festività nazionali",
        "Puoi inserire eventi aziendali con il pulsante '+ Aggiungi Evento':",
        "• Straordinario — registra le ore di straordinario di un dipendente (data + orario)",
        "• Malattia — registra le giornate di assenza per malattia di un dipendente",
        "• Ferie Aziendali — segna i periodi di chiusura aziendale: in queste date i dipendenti non potranno richiedere ferie o permessi e riceveranno il messaggio 'In questa data ci sono le Ferie Aziendali'",
        "Premi 'Genera report mensile' a fine mese per inviarlo al consulente per il calcolo delle buste paga"
      ]
    },
    {
      icon: CreditCard,
      title: "Abbonamento",
      description: "Il tuo piano e le opzioni di upgrade:",
      items: [
        "Vedi il piano attuale e il numero di dipendenti incluso",
        "Confronta i piani disponibili: Base, Standard, Premium, Enterprise",
        "Effettua il pagamento con Carta di Credito (Stripe)",
        "Cambia piano in qualsiasi momento — paghi solo la differenza",
        "Per logo aziendale, tema colore, festività locali, prospetto dipendenti e notifiche email passa al piano Standard o superiore"
      ]
    }
  ];

  // Instructions for Dipendente
  const dipendenteInstructions = [
    {
      icon: LayoutDashboard,
      title: "Home",
      description: "La tua pagina principale dove puoi:",
      items: [
        ...(settings?.mostra_saldi_dipendenti ? ["Visualizzare il riepilogo delle tue ferie e permessi dell'anno"] : []),
        "Vedere lo stato delle tue richieste (in attesa, approvate, rifiutate)",
        "Creare una nuova richiesta di ferie o permesso con il pulsante '+ Nuova Richiesta'",
        "Consultare lo storico delle tue richieste recenti",
        "🗑️ Cancella le richieste in attesa se hai commesso errori (date sbagliate, note errate)"
      ]
    },
    {
      icon: Calendar,
      title: "Calendario",
      description: "Visualizza il calendario aziendale:",
      items: [
        "Vedi le ferie e permessi approvati di tutto il team",
        "Consulta le festività nazionali e locali",
        "Visualizza eventi speciali (straordinari, malattie, ecc.)"
      ]
    }
  ];

  // Instructions for Amministrativo
  const amministrativoInstructions = [
    {
      icon: LayoutDashboard,
      title: "Home",
      description: "La tua pagina principale dove puoi:",
      items: [
        ...(settings?.mostra_saldi_dipendenti ? ["Visualizzare il tuo riepilogo personale di ferie e permessi"] : []),
        "Creare nuove richieste di ferie o permessi",
        "Consultare lo storico delle tue richieste"
      ]
    },
    {
      icon: FileText,
      title: "Richieste",
      description: "Gestisci le tue richieste personali:",
      items: [
        "Visualizza lo storico delle tue richieste",
        "Filtra per stato: Tutte / In attesa / Approvate / Rifiutate",
        "Crea nuove richieste di ferie o permessi",
        "🗑️ Cancella le richieste in attesa se hai commesso errori"
      ]
    },
    {
      icon: Users,
      title: "Dipendenti",
      description: "Visualizza l'elenco del team:",
      items: [
        "Consulta l'elenco completo dei dipendenti",
        "Se autorizzato dal Titolare: configura ferie e permessi dei dipendenti",
        "Visualizza i dati di ogni utente (nome, ruolo, posizione)"
      ]
    },
    {
      icon: Calendar,
      title: "Calendario",
      description: "Gestisci il calendario aziendale:",
      items: [
        "Vedi le ferie e permessi approvati del team",
        "Se autorizzato: aggiungi eventi come Straordinario o Malattia",
        "Consulta festività nazionali e locali"
      ]
    },
    {
      icon: Settings,
      title: "Impostazioni",
      description: "Accedi alle impostazioni in base ai permessi assegnati dal Titolare:",
      items: [
        "Email: gestisci le tue notifiche personali",
        "Se autorizzato: Settimana lavorativa, Festività",
        "I permessi vengono assegnati dal Titolare nella sezione 'Gestione Dipendenti'"
      ]
    },
    {
      icon: Shield,
      title: "Permessi Speciali",
      description: "Il Titolare può assegnarti permessi per:",
      items: [
        "Configurare ferie e permessi dei dipendenti (icona calendario verde)",
        "Aggiungere eventi nel calendario (straordinari, malattie)"
      ]
    }
  ];

  // Instructions for Titolare on Enterprise plan (top-tier — all features unlocked)
  const titolareEnterpriseInstructions = [
    {
      icon: LayoutDashboard,
      title: "Home",
      description: "Il punto di partenza della tua azienda:",
      items: [
        "Vedi quante richieste sono in attesa di approvazione e accedi rapidamente per approvarle/rifiutarle",
        "Visualizza il Prospetto Ferie Dipendenti: tabella con ferie residue, ferie approvate, permessi residui per ogni dipendente",
        "Tieni d'occhio il numero di dipendenti registrati"
      ]
    },
    {
      icon: FileText,
      title: "Richieste",
      description: "Gestione delle richieste del team:",
      items: [
        "Visualizza tutte le richieste di ferie e permessi dei tuoi dipendenti e amministrativi",
        "Filtra per stato: In attesa / Approvate / Rifiutate",
        "Approva o rifiuta direttamente dalla lista con un click",
        "Aggiungi una nota di risposta se vuoi spiegare il motivo"
      ]
    },
    {
      icon: Users,
      title: "Dipendenti",
      description: "Gestione completa del personale aziendale:",
      items: [
        "Vedi tutti i dipendenti e gli amministrativi che si sono registrati con il tuo codice",
        "Configura ferie/permessi del singolo dipendente per il calcolo accurato del prospetto residue",
        "Gestisci i permessi degli Amministrativi (autorizzazioni a eseguire azioni per conto tuo)",
        "Quando un dipendente lascia l'azienda, premi il cestino rosso per eliminarlo"
      ]
    },
    {
      icon: Calendar,
      title: "Calendario",
      description: "Il calendario condiviso con i tuoi dipendenti:",
      items: [
        "Vedi tutte le ferie e i permessi approvati del team in un colpo d'occhio",
        "Anche i dipendenti vedono lo stesso calendario, così sanno chi è assente",
        "Consulta le festività nazionali e quelle locali che hai aggiunto",
        "Puoi inserire eventi aziendali con il pulsante '+ Aggiungi Evento':",
        "• Straordinario — registra le ore di straordinario di un dipendente (data + orario)",
        "• Malattia — registra le giornate di assenza per malattia di un dipendente",
        "• Ferie Aziendali — segna i periodi di chiusura aziendale",
        "• Data Bloccata — blocca date specifiche in cui i dipendenti non potranno inviare richieste",
        "Premi 'Genera report mensile' a fine mese per inviarlo al consulente per il calcolo delle buste paga"
      ]
    },
    {
      icon: Settings,
      title: "Impostazioni",
      description: "Configurazione completa dell'app per il tuo Piano Enterprise:",
      items: [
        "Generale: nome azienda, logo aziendale e Codice Invito per i nuovi dipendenti",
        "Aspetto: scegli il colore tema dell'applicazione",
        "Settimana: giorno di inizio/fine + chiusura infrasettimanale",
        "Festività: festività locali della tua zona (es. Santo Patrono)",
        "Limiti: configura fino a quando i dipendenti possono richiedere ferie/permessi + date bloccate",
        "Email: notifiche via email oltre alla campanella nell'app",
        "Visibilità: scegli se mostrare il prospetto ferie residue anche nella home dei dipendenti"
      ]
    },
    {
      icon: Shield,
      title: "Ruoli Amministrativi (esclusivi Enterprise)",
      description: "Delega operazioni quotidiane ai tuoi collaboratori di fiducia:",
      items: [
        "Aggiungi dipendenti come 'Amministrativo' invece che semplici 'Dipendenti' al momento della registrazione",
        "Vai in Elenco utenti → icona 'Scudo' sull'utente Amministrativo",
        "Scegli quali azioni può eseguire al posto tuo (approvare richieste, gestire ferie/permessi, ecc.)",
        "L'Amministrativo accede con le stesse credenziali e vede solo le funzioni che gli hai abilitato"
      ]
    },
    {
      icon: Eye,
      title: "Prospetto ferie residue",
      description: "Calcolo automatico delle ferie residue per ogni dipendente:",
      items: [
        "Configura ferie/permessi annui di ogni dipendente da Elenco utenti → icona Calendario",
        "L'app calcola automaticamente le ferie residue sottraendo quelle già richieste/approvate",
        "Tu vedi sempre il prospetto completo nella tua Home",
        "In Impostazioni > Visibilità decidi se i singoli dipendenti possono vedere le proprie ferie residue nella loro home"
      ]
    },
    {
      icon: Mail,
      title: "Supporto via e-mail",
      description: "Hai accesso al supporto prioritario via email:",
      items: [
        "Scrivi a support@ferie-e-permessi.it dalla pagina Istruzioni in fondo (pulsante in basso)",
        "Riceverai una risposta dedicata, con priorità rispetto agli altri piani"
      ]
    },
    {
      icon: CreditCard,
      title: "Abbonamento",
      description: "Il tuo piano e le opzioni di upgrade:",
      items: [
        "Vedi il piano attuale e il numero di dipendenti incluso",
        "Confronta i piani disponibili: Base, Standard, Premium, Enterprise",
        "Effettua il pagamento con Carta di Credito (Stripe)",
        "Cambia piano in qualsiasi momento — paghi solo la differenza",
        "Sei sul piano Enterprise: hai accesso a tutte le funzioni dell'app"
      ]
    },
    {
      icon: Key,
      title: "Codice Invito",
      description: "Il modo per far entrare i dipendenti nella tua azienda:",
      items: [
        "Trova il codice in Impostazioni > Generale",
        "Condividilo con i tuoi dipendenti via WhatsApp, email o di persona",
        "I dipendenti scaricano l'app e si registrano inserendo questo codice",
        "Solo nel piano Enterprise puoi farli registrare anche come 'Amministrativo'"
      ]
    }
  ];

  // Instructions for Titolare on Premium plan (Standard + 4 funzioni Premium)
  const titolarePremiumInstructions = [
    {
      icon: LayoutDashboard,
      title: "Home",
      description: "Il punto di partenza della tua azienda:",
      items: [
        "Vedi quante richieste sono in attesa di approvazione e accedi rapidamente per approvarle/rifiutarle",
        "Tieni d'occhio il numero di dipendenti registrati"
      ]
    },
    {
      icon: FileText,
      title: "Richieste",
      description: "Gestione delle richieste del team:",
      items: [
        "Visualizza tutte le richieste di ferie e permessi dei tuoi dipendenti",
        "Filtra per stato: In attesa / Approvate / Rifiutate",
        "Approva o rifiuta direttamente dalla lista con un click",
        "Aggiungi una nota di risposta se vuoi spiegare il motivo"
      ]
    },
    {
      icon: Users,
      title: "Dipendenti",
      description: "Tieni sotto controllo chi fa parte della tua azienda:",
      items: [
        "Vedi tutti i dipendenti che si sono registrati con il tuo codice",
        "Quando un dipendente lascia l'azienda, premi il cestino rosso per eliminarlo",
        "L'app ti chiede sempre conferma prima di eliminare"
      ]
    },
    {
      icon: Calendar,
      title: "Calendario",
      description: "Il calendario condiviso con i tuoi dipendenti:",
      items: [
        "Vedi tutte le ferie e i permessi approvati del team in un colpo d'occhio",
        "Anche i dipendenti vedono lo stesso calendario, così sanno chi è assente",
        "Consulta le festività nazionali e quelle locali che hai aggiunto",
        "Puoi inserire eventi aziendali con il pulsante '+ Aggiungi Evento':",
        "• Straordinario — registra le ore di straordinario di un dipendente (data + orario)",
        "• Malattia — registra le giornate di assenza per malattia di un dipendente",
        "• Ferie Aziendali — segna i periodi di chiusura aziendale: in queste date i dipendenti non potranno richiedere ferie o permessi",
        "• Data Bloccata (novità Premium) — blocca una o più date specifiche in cui i dipendenti non potranno inviare richieste. Vedranno il messaggio 'Questa data è bloccata, prova un'altra data'",
        "Premi 'Genera report mensile' a fine mese per inviarlo al consulente per il calcolo delle buste paga"
      ]
    },
    {
      icon: Settings,
      title: "Impostazioni",
      description: "Configurazione dell'app per il tuo Piano Premium:",
      items: [
        "Generale: nome azienda, logo aziendale e Codice Invito per i nuovi dipendenti",
        "Aspetto: scegli il colore tema dell'applicazione",
        "Settimana: giorno di inizio/fine settimana lavorativa + chiusura infrasettimanale",
        "Festività: festività locali della tua zona (es. Santo Patrono)",
        "Limiti: configura fino a quando i dipendenti possono richiedere ferie e permessi + date bloccate in cui non potranno inviare richieste",
        "Email: attiva le notifiche via email oltre alla campanella nell'app per te e per i dipendenti",
        "La tab Visibilità è disponibile solo dal piano Enterprise (la vedi in grigio)"
      ]
    },
    {
      icon: Mail,
      title: "Notifiche via e-mail",
      description: "Oltre alla campanella nell'app, ricevi le notifiche direttamente in casella di posta:",
      items: [
        "Apri Impostazioni > Email",
        "Scegli quali eventi vuoi ricevere via email (nuove richieste, approvazioni, eventi in calendario, ecc.)",
        "I dipendenti possono attivare le proprie notifiche email in modo indipendente"
      ]
    },
    {
      icon: Clock,
      title: "Limiti e date bloccate",
      description: "Controllo fine sul quando i dipendenti possono inviare richieste:",
      items: [
        "Vai in Impostazioni > Limiti per impostare fino a quando nel futuro i dipendenti possono richiedere ferie/permessi (es. solo fino a 3 mesi avanti)",
        "Oppure crea una Data Bloccata direttamente dal Calendario con '+ Aggiungi Evento' → 'Data Bloccata': seleziona il periodo e i dipendenti non potranno inviare richieste in quelle date",
        "Il dipendente che prova a inviare una richiesta su una data bloccata riceve il messaggio: 'Questa data è bloccata, prova un'altra data'"
      ]
    },
    {
      icon: CreditCard,
      title: "Abbonamento",
      description: "Il tuo piano e le opzioni di upgrade:",
      items: [
        "Sei sul Piano Premium: include tutte le funzioni del Standard + notifiche email, date bloccate, periodo di richiesta configurabile e supporto via e-mail",
        "Per ruoli Amministrativi e prospetto ferie residue dei dipendenti passa al piano Enterprise",
        "Pagamento sicuro con Carta di Credito (Stripe), PayPal o Bonifico Bancario",
        "Quando passi al piano Enterprise paghi solo la differenza di attivazione (€10,00)"
      ]
    },
    {
      icon: Key,
      title: "Codice Invito",
      description: "Il modo per far entrare i dipendenti nella tua azienda:",
      items: [
        "Trova il codice in Impostazioni > Generale",
        "Condividilo con i tuoi dipendenti via WhatsApp, email o di persona",
        "I dipendenti scaricano l'app e si registrano inserendo questo codice",
        "Una volta registrati fanno subito parte della tua azienda"
      ]
    }
  ];

  // Instructions for Titolare on Standard plan (Base + 4 funzioni Standard)
  const titolareStandardInstructions = [
    {
      icon: LayoutDashboard,
      title: "Home",
      description: "Il punto di partenza della tua azienda:",
      items: [
        "Vedi quante richieste sono in attesa di approvazione e accedi rapidamente per approvarle/rifiutarle",
        "Tieni d'occhio il numero di dipendenti registrati"
      ]
    },
    {
      icon: FileText,
      title: "Richieste",
      description: "Gestione delle richieste del team:",
      items: [
        "Visualizza tutte le richieste di ferie e permessi dei tuoi dipendenti",
        "Filtra per stato: In attesa / Approvate / Rifiutate",
        "Approva o rifiuta direttamente dalla lista con un click",
        "Aggiungi una nota di risposta se vuoi spiegare il motivo"
      ]
    },
    {
      icon: Users,
      title: "Dipendenti",
      description: "Tieni sotto controllo chi fa parte della tua azienda:",
      items: [
        "Vedi tutti i dipendenti che si sono registrati con il tuo codice",
        "Quando un dipendente lascia l'azienda, premi il cestino rosso per eliminarlo",
        "L'app ti chiede sempre conferma prima di eliminare"
      ]
    },
    {
      icon: Calendar,
      title: "Calendario",
      description: "Il calendario condiviso con i tuoi dipendenti:",
      items: [
        "Vedi tutte le ferie e i permessi approvati del team in un colpo d'occhio",
        "Anche i dipendenti vedono lo stesso calendario, così sanno chi è assente",
        "Consulta le festività nazionali e quelle locali che hai aggiunto",
        "Puoi inserire eventi aziendali con il pulsante '+ Aggiungi Evento':",
        "• Straordinario — registra le ore di straordinario di un dipendente (data + orario)",
        "• Malattia — registra le giornate di assenza per malattia di un dipendente",
        "• Ferie Aziendali — segna i periodi di chiusura aziendale: in queste date i dipendenti non potranno richiedere ferie o permessi",
        "Premi 'Genera report mensile' a fine mese per inviarlo al consulente per il calcolo delle buste paga"
      ]
    },
    {
      icon: Settings,
      title: "Impostazioni",
      description: "Configurazione dell'app per il tuo Piano Standard:",
      items: [
        "Generale: nome azienda, logo aziendale e Codice Invito per i nuovi dipendenti",
        "Aspetto: scegli il colore tema dell'applicazione, personalizzando l'app a immagine della tua azienda",
        "Settimana: configura il giorno di inizio/fine della tua settimana lavorativa e l'eventuale giorno di chiusura infrasettimanale (es. mercoledì pomeriggio)",
        "Festività: aggiungi festività locali specifiche per la tua zona (es. Santo Patrono)"
      ]
    },
    {
      icon: Building,
      title: "Logo aziendale & Aspetto",
      description: "Personalizza l'app con l'identità della tua azienda:",
      items: [
        "Carica il logo aziendale da Impostazioni > Generale (PNG, JPG o SVG, max 2MB)",
        "Il logo verrà mostrato nella barra laterale e nelle pagine di accesso",
        "In Impostazioni > Aspetto scegli il colore tema preferito"
      ]
    },
    {
      icon: Flag,
      title: "Festività locali",
      description: "Aggiungi festività della tua zona oltre a quelle nazionali:",
      items: [
        "Vai in Impostazioni > Festività",
        "Inserisci nome, giorno e mese (es. \"Santo Patrono\" — 19 marzo)",
        "Le festività appariranno automaticamente nel calendario per tutti"
      ]
    },
    {
      icon: CreditCard,
      title: "Abbonamento",
      description: "Il tuo piano e le opzioni di upgrade:",
      items: [
        "Sei sul Piano Standard: include tutte le funzioni del Base + logo, tema, chiusura infrasettimanale e festività locali",
        "Per notifiche email, date bloccate, configurazione del periodo di richiesta e supporto via e-mail passa al piano Premium",
        "Per ruoli Amministrativi e prospetto ferie residue dei dipendenti passa al piano Enterprise",
        "Pagamento sicuro con Carta di Credito (Stripe), PayPal o Bonifico Bancario",
        "Quando passi a un piano superiore paghi solo la differenza di attivazione"
      ]
    },
    {
      icon: Key,
      title: "Codice Invito",
      description: "Il modo per far entrare i dipendenti nella tua azienda:",
      items: [
        "Trova il codice in Impostazioni > Generale",
        "Condividilo con i tuoi dipendenti via WhatsApp, email o di persona",
        "I dipendenti scaricano l'app e si registrano inserendo questo codice",
        "Una volta registrati fanno subito parte della tua azienda"
      ]
    }
  ];

  // Instructions for Titolare
  const titolareInstructions = [
    {
      icon: LayoutDashboard,
      title: "Home",
      description: "Il centro di controllo della tua azienda:",
      items: [
        "Clicca su 'Richieste in Attesa' per andare direttamente ad approvare/rifiutare",
        "Visualizza il numero totale di dipendenti",
        "Consulta il 'Prospetto Ferie Dipendenti' con la situazione completa di ogni dipendente",
        "Vedi per ogni dipendente: richieste in attesa, ferie approvate, ferie residue, permessi approvati, ore residue"
      ]
    },
    {
      icon: FileText,
      title: "Richieste",
      description: "Gestione completa delle richieste:",
      items: [
        "Visualizza tutte le richieste di tutti i dipendenti",
        "Filtra per stato: Tutte / In attesa / Approvate / Rifiutate",
        "Approva o rifiuta direttamente dalla lista",
        "Puoi anche creare richieste per te stesso"
      ]
    },
    {
      icon: Users,
      title: "Dipendenti",
      description: "Amministra il tuo team (accessibile dal menu laterale):",
      items: [
        "📅 Icona Calendario: configura ferie e permessi annuali del dipendente",
        "🛡️ Icona Scudo: assegna permessi speciali agli Amministrativi",
        "✏️ Icona Matita: modifica dati utente (nome, ruolo, posizione)",
        "🗑️ Icona Cestino: elimina utenti"
      ]
    },
    {
      icon: Shield,
      title: "Permessi Amministrativi",
      description: "Cliccando sullo scudo di un Amministrativo puoi autorizzarlo a:",
      items: [
        "Configurare ferie e permessi dei dipendenti",
        "Aggiungere eventi nel calendario (straordinari, malattie)"
      ]
    },
    {
      icon: Calendar,
      title: "Calendario",
      description: "Calendario aziendale completo:",
      items: [
        "Vedi le ferie e permessi approvati del team",
        "Aggiungi eventi speciali (Straordinario, Malattia, Formazione, ecc.)",
        "Visualizza festività nazionali e locali"
      ]
    },
    {
      icon: Settings,
      title: "Impostazioni",
      description: "Configurazione completa dell'applicazione:",
      items: [
        "Generale: Nome azienda, logo e Codice Invito per i nuovi dipendenti",
        "Aspetto: Scegli il colore tema dell'applicazione",
        "Settimana: Configura giorni lavorativi e chiusura infrasettimanale",
        "Limiti: Imposta fino a quando si possono richiedere ferie/permessi",
        "Festività: Aggiungi festività locali (es. Santo Patrono)",
        "Email: Configura le notifiche che vuoi ricevere",
        "Visibilità: Decidi se i dipendenti possono vedere le proprie ferie residue"
      ]
    },
    {
      icon: Eye,
      title: "Visibilità Ferie Residue",
      description: "Controlla cosa vedono i dipendenti:",
      items: [
        "Vai in Impostazioni > Visibilità",
        "Attiva/disattiva la visualizzazione delle ferie residue per i dipendenti",
        "Se disattivato, i dipendenti possono solo inviare richieste senza vedere i saldi"
      ]
    },
    {
      icon: CreditCard,
      title: "Abbonamento",
      description: "Gestisci il tuo abbonamento dalla pagina dedicata:",
      items: [
        "Visualizza il piano attuale e i limiti dipendenti",
        "Scegli tra i piani: Base, Standard, Premium, Enterprise",
        "Effettua l'upgrade con Carta di Credito (Stripe) o PayPal",
        "Richiedi pagamento tramite Bonifico Bancario",
        "Piano Free: gratuito fino a 5 dipendenti per le nuove aziende"
      ]
    },
    {
      icon: Key,
      title: "Codice Invito",
      description: "Gestisci l'accesso alla tua azienda:",
      items: [
        "Trova il codice invito nelle Impostazioni > Generale",
        "Condividi il codice con i nuovi dipendenti per la registrazione",
        "I dipendenti useranno questo codice durante la registrazione"
      ]
    }
  ];

  // Select instructions based on role and plan
  const currentPiano = settings?.piano || "base";
  const getInstructions = () => {
    if (isBasePlan) {
      if (ruolo === "titolare") return titolareBaseInstructions;
      // Dipendente / amministrativo on Base: same simplified set
      return dipendenteBaseInstructions;
    }
    switch (ruolo) {
      case "titolare":
        if (currentPiano === "standard") return titolareStandardInstructions;
        if (currentPiano === "premium") return titolarePremiumInstructions;
        if (currentPiano === "enterprise") return titolareEnterpriseInstructions;
        return titolareInstructions;
      case "amministrativo":
        return amministrativoInstructions;
      default:
        return dipendenteInstructions;
    }
  };

  const getRoleTitle = () => {
    switch (ruolo) {
      case "titolare":
        return "Titolare";
      case "amministrativo":
        return "Amministrativo";
      default:
        return "Dipendente";
    }
  };

  const getRoleIcon = () => {
    switch (ruolo) {
      case "titolare":
        return Crown;
      case "amministrativo":
        return Shield;
      default:
        return Briefcase;
    }
  };

  const instructions = getInstructions();
  const RoleIcon = getRoleIcon();

  return (
    <Layout>
      <div className="space-y-8 animate-fade-in max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="inline-flex items-center justify-center p-3 rounded-full mb-4" style={{ backgroundColor: 'var(--theme-100)' }}>
            <RoleIcon className="h-8 w-8" style={{ color: 'var(--theme-600)' }} />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">
            Guida per {getRoleTitle()}
          </h1>
          <p className="text-slate-500">
            Scopri come utilizzare al meglio l'applicazione
          </p>
        </div>

        {/* Instructions Cards */}
        <div className="space-y-6">
          {instructions.map((section, index) => {
            const Icon = section.icon;
            return (
              <Card key={index} className="border-slate-200 overflow-hidden">
                <CardHeader className="pb-3" style={{ backgroundColor: 'var(--theme-50)' }}>
                  <CardTitle className="flex items-center gap-3 text-lg">
                    <div className="p-2 rounded-lg" style={{ backgroundColor: 'var(--theme-100)' }}>
                      <Icon className="h-5 w-5" style={{ color: 'var(--theme-600)' }} />
                    </div>
                    {section.title}
                  </CardTitle>
                  <CardDescription className="text-slate-600">
                    {section.description}
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-4">
                  <ul className="space-y-2">
                    {section.items.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 mt-0.5 shrink-0" style={{ color: 'var(--theme-500)' }} />
                        <span className="text-slate-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Reactivation panel for hidden section banners */}
        <BannerVisibilityCard user={user} isBasePlan={isBasePlan} />

        {/* Disclaimer Section */}
        <Separator className="my-8" />
        <Card className="border-amber-200 bg-amber-50">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-amber-800">
              <AlertTriangle className="h-5 w-5" />
              Avviso Importante
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-amber-800 space-y-2">
            <p>I dati visualizzati sono puramente indicativi e potrebbero non rispecchiare la situazione reale.</p>
            <p>Non hanno alcun valore legale e non possono essere utilizzati per fini ufficiali.</p>
            <p>Per i dati certificati fare sempre riferimento alla busta paga o alla documentazione fornita dall'azienda.</p>
            <p>Da tali informazioni non deriva alcun diritto né obbligo per nessuna delle parti.</p>
          </CardContent>
        </Card>

        {/* Footer - Help & Credits */}
        <Separator className="my-8" />
        <div className="text-center space-y-6 pb-8">
          {/* Logo */}
          <div className="flex justify-center">
            <img 
              src="/logo_ferie_permessi.jpg" 
              alt="Ferie e Permessi" 
              className="h-32 w-auto object-contain rounded-lg shadow-md"
            />
          </div>
          
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-slate-900 flex items-center justify-center gap-2">
              <HelpCircle className="h-5 w-5" style={{ color: 'var(--theme-500)' }} />
              Hai bisogno di aiuto?
            </h3>
            <p className="text-slate-600">
              Contatta il nostro supporto per qualsiasi domanda o problema
            </p>
            {(settings?.piano === "premium" || settings?.piano === "enterprise") ? (
              <Button 
                className="btn-theme"
                onClick={() => {
                  const subject = `Richiesta supporto — Azienda ${settings?.nome_azienda || ""}`.trim();
                  const planLabel = { base: "Base", standard: "Standard", premium: "Premium", enterprise: "Enterprise" }[settings?.piano] || "Base";
                  const ruoloLabel = { titolare: "Titolare", amministrativo: "Amministrativo", dipendente: "Dipendente" }[user?.ruolo] || "";
                  const body = [
                    "Ciao team Ferie e Permessi,",
                    "",
                    "ho bisogno di aiuto. Di seguito il riepilogo della mia situazione:",
                    "",
                    "━━━━━━━━━━━━━━━━━━━━━━━━━",
                    "🧾 I MIEI DATI",
                    "━━━━━━━━━━━━━━━━━━━━━━━━━",
                    `• Nome: ${user?.nome || ""} ${user?.cognome || ""}`.trim(),
                    `• Email: ${user?.email || ""}`,
                    `• Azienda: ${settings?.nome_azienda || ""}`,
                    `• Piano: ${planLabel}`,
                    `• Ruolo: ${ruoloLabel}`,
                    "",
                    "━━━━━━━━━━━━━━━━━━━━━━━━━",
                    "❓ IN COSA POSSIAMO AIUTARTI?",
                    "(metti una X accanto al problema — più di uno se necessario)",
                    "━━━━━━━━━━━━━━━━━━━━━━━━━",
                    "",
                    "[ ] 1. Un dipendente non riesce a registrarsi con il codice invito",
                    "[ ] 2. Una richiesta di ferie/permesso non si invia o dà errore",
                    "[ ] 3. Il calendario non mostra correttamente gli eventi",
                    "[ ] 4. Problemi con il pagamento o la fatturazione",
                    "[ ] 5. Voglio fare l'upgrade del piano ma non funziona",
                    "[ ] 6. Il logo aziendale non si carica",
                    "[ ] 7. Non ricevo le notifiche email",
                    "[ ] 8. Il report mensile non si genera correttamente",
                    "[ ] 9. Ho un suggerimento / voglio proporre una nuova funzionalità",
                    "[ ] 10. Report con errori",
                    "[ ] 11. Altro (descrivi sotto)",
                    "",
                    "━━━━━━━━━━━━━━━━━━━━━━━━━",
                    "✍️ DESCRIVI IL PROBLEMA CON PAROLE TUE",
                    "(aggiungi qui qualsiasi dettaglio utile: cosa stavi facendo, cosa ti aspettavi, cosa è successo)",
                    "━━━━━━━━━━━━━━━━━━━━━━━━━",
                    "",
                    "[scrivi qui...]",
                    "",
                    "━━━━━━━━━━━━━━━━━━━━━━━━━",
                    "",
                    "Grazie per l'aiuto!"
                  ].join("\n");
                  const mailto = `mailto:support@ferie-e-permessi.it?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
                  window.location.href = mailto;
                }}
                data-testid="contact-support-btn"
              >
                <Mail className="h-4 w-4 mr-2" />
                support@ferie-e-permessi.it
              </Button>
            ) : (
              <p className="text-slate-700 font-medium flex items-center justify-center gap-2" data-testid="contact-support-email">
                <Mail className="h-4 w-4 text-slate-500" />
                support@ferie-e-permessi.it
              </p>
            )}
          </div>

          <div className="pt-4 border-t border-slate-200">
            <p className="text-sm text-slate-400">
              Creato da <span className="font-semibold text-slate-600">LuiCas</span> ™
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
}
